import { TypedDocumentNode, gql } from '@apollo/client';
import isEqual from 'lodash.isequal';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  SaleType,
  useCardsOnSale,
} from '@sorare/core/src/hooks/search/useCardsOnSale';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';
import {
  PlayerSearchViewAttributes,
  StatsRarity,
} from '@sorare/core/src/lib/playerSearchView';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerDrawerToken } from 'components/token/PlayerDrawerToken';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { Auctions } from '../Auctions';
import { Header } from '../Header';
import { ManagerSales } from '../ManagerSales';
import { Block, PlayerDrawerTokenSkeleton } from '../ui';
import type {
  PlayerMarketDrawerContentQuery,
  PlayerMarketDrawerContentQueryVariables,
} from './__generated__/index.graphql';
import { useSortSecondaryOfferSlugs } from './useSortSecondaryOfferSlugs';

const Root = styled(Vertical).attrs({
  gap: 0,
})`
  background-color: var(--c-black);
  flex-grow: 1;
  width: 100%;
  @media ${tabletAndAbove} {
    width: 480px;
  }
`;

const optionalFilters = joinFiltersWithAnd([
  `sale.type:${SaleType.PrimaryOffer}`,
  `sale.type:${SaleType.EnglishAuction}`,
]);

type Card = PlayerMarketDrawerContentQuery['anyCards'][number];

const PLAYER_MARKET_DRAWER_QUERY = gql`
  query PlayerMarketDrawerContentQuery(
    $slugs: [String!]!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
    $onlyPrimary: Boolean = false
  ) {
    anyCards(slugs: $slugs) {
      slug
      ...PlayerMarketDrawerAuctions_anyCard
      ...PlayerDrawerToken_anyCard
      ...PlayerMarketDrawerManagerSales_anyCard
      ...useSortSecondaryOfferSlugs_anyCard
    }
  }
  ${Auctions.fragments.anyCard}
  ${PlayerDrawerToken.fragments.anyCard}
  ${ManagerSales.fragments.anyCard}
  ${useSortSecondaryOfferSlugs.fragments.anyCard}
` as TypedDocumentNode<
  PlayerMarketDrawerContentQuery,
  PlayerMarketDrawerContentQueryVariables
>;

type Props = {
  playerSlug: string | null;
  skip?: boolean;
  canUseConversionCredit?: boolean;
  onClose: () => void;
};

export const Content = ({
  playerSlug,
  skip,
  canUseConversionCredit,
  onClose,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();

  const { googlePlayStoreCountryCode, isAndroidApp, isIosApp } =
    useIsMobileApp();

  const [{ seasonality, rarity }, setFilters] = useState({
    seasonality: statsManager[PlayerSearchViewAttributes.SEASONALITY],
    rarity: statsManager[PlayerSearchViewAttributes.RARITY],
  });

  const cardIndex = statsManager[PlayerSearchViewAttributes.CARDS_SORT];

  const [{ auctions, primaryOffer }, setCards] = useState<{
    auctions: Card[];
    primaryOffer: Card | null;
  }>({
    auctions: [],
    primaryOffer: null,
  });

  const {
    loading: algoliaLoading,
    slugs,
    auctionSlugs,
    primaryOfferSlugs,
    secondaryOfferSlugs: secondaryOfferSlugsFromAlgolia,
    nbHits,
  } = useCardsOnSale({
    ...(playerSlug && { playerSlugs: [playerSlug] }),
    rarities: [rarity],
    inSeasonEligible: seasonality === So5LeaderboardSeasonality.IN_SEASON,
    limit: 40,
    index: cardIndex,
    optionalFilters,
    skip,
  });

  const managerSalesCount =
    nbHits - auctionSlugs.length - primaryOfferSlugs.length;

  const skipGql = algoliaLoading || skip;
  const { data, loading: gqlLoading } = useQuery(PLAYER_MARKET_DRAWER_QUERY, {
    variables: {
      slugs,
      googlePlayStoreCountryCode,
      isAndroidApp,
      isIosApp,
      onlyPrimary: false,
    },
    skip: skipGql,
  });

  const secondaryOfferSlugs = useSortSecondaryOfferSlugs({
    slugs: secondaryOfferSlugsFromAlgolia,
    anyCards: data?.anyCards || [],
    cardIndex,
  });

  const managerSales = secondaryOfferSlugs
    .map(s => data?.anyCards.find(c => c.slug === s))
    .filter(Boolean);

  if (data?.anyCards && ((!algoliaLoading && !gqlLoading) || skipGql)) {
    const auctionCards = data.anyCards.filter(
      ({ slug, latestEnglishAuction }) =>
        auctionSlugs.includes(slug) && latestEnglishAuction
    );
    const auctionCardsByAscendingEndDate = auctionCards.sort((a, b) => {
      return (
        new Date(a.latestEnglishAuction!.endDate).getTime() -
        new Date(b.latestEnglishAuction!.endDate).getTime()
      );
    });
    const primaryOfferCard =
      data.anyCards.find(card => primaryOfferSlugs.includes(card.slug)) || null;

    if (
      !isEqual(auctions, auctionCardsByAscendingEndDate) ||
      !isEqual(primaryOffer, primaryOfferCard)
    ) {
      setCards({
        auctions: auctionCardsByAscendingEndDate,
        primaryOffer: primaryOfferCard,
      });
    }
  }

  return (
    <Root>
      <Header
        filters={{ seasonality, rarity }}
        setRarity={(r: StatsRarity) => {
          setFilters(prev => ({ ...prev, rarity: r }));
        }}
        setSeasonality={(s: So5LeaderboardSeasonality) => {
          setFilters(prev => ({ ...prev, seasonality: s }));
        }}
        onClose={onClose}
      />
      <Auctions
        slugs={auctionSlugs}
        anyCards={auctions}
        canUseConversionCredit={canUseConversionCredit}
      />
      {!!primaryOfferSlugs?.length && (
        <Block>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              id="PlayerMarketDrawer.content.primaryOffer"
              defaultMessage="New Card"
            />
          </LabelM>
          {primaryOffer ? (
            <PlayerDrawerToken
              anyCard={primaryOffer}
              bestValue
              canUseConversionCredit={canUseConversionCredit}
            />
          ) : (
            <PlayerDrawerTokenSkeleton />
          )}
        </Block>
      )}
      <ManagerSales
        anyCards={managerSales}
        slugs={secondaryOfferSlugs}
        count={managerSalesCount}
      />
    </Root>
  );
};
